<!--
    AUTHOR: Ryan Lang
    LATEST UPDATED DATE: 06/17/2026
    DESCRIPTION: Dashboard widget/card that represents an graphed value according to defined parameter and date/time range values
    ADDITIONAL INFORMATION: //
-->

<!--
    DESCRIPTION: Dashboard widget/card that represents a graphed value according to
    defined parameter and date/time range values.
-->
<template>

  <div class="widget-header rounded-t-md bg-white px-2 shadow flex justify-between w-full min-h-[40px] z-1 items-center">
    <span class="widget-title text-xs xl:text-sm overflow-hidden" @click="isInfoModalOpen = true">
      {{ props.Settings.FullName }}
      <InformationCircleIcon
        class="h-4 w-4 text-primary/40 inline hover:cursor-pointer"        
      />
    </span>

    <span class="widget-actions">
      <Popover class="relative">
        <PopoverButton>
          <EllipsisVerticalIcon class="h-full w-5 text-black hover:cursor-pointer" />
        </PopoverButton>

        <transition
          enter-active-class="transition duration-50 ease-out"
          enter-from-class="translate-y-4 opacity-0"
          enter-to-class="translate-y-0 opacity-100"
          leave-active-class="transition duration-50 ease-in"
          leave-from-class="translate-y-0 opacity-100"
          leave-to-class="translate-y-4 opacity-0"
        >
          <PopoverPanel
            v-slot="{ close }"
            class="widget-menu absolute z-20 top-[40px] right-0 backdrop-blur bg-[#eceff8] opacity-90 text-primary text-left rounded-md shadow-xl p-2 w-[140px] text-xs"
          >
            <div>
              <button
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="() => { reloadWidgetData(); close(); }"
              >
                <ArrowPathIcon class="inline w-4 mr-2" /> Refresh
              </button>

              <button
                v-if="isGraphWidget"
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="() => { redirectToWidget(); close(); }"
              >
                <ArrowsPointingOutIcon class="inline w-4 mr-2" /> Fullscreen
              </button>

              <button
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="() => { editWidget(); close(); }"
              >
                <PencilIcon class="inline w-4 mr-2" /> Edit
              </button>

              <button
                v-if="isGraphWidget"
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="async () => { await exportData(); close(); }"
              >
                <ArrowUpTrayIcon class="inline w-4 mr-2" /> Export Data
              </button>

              <button
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="() => { duplicateWidget(); close(); }"
              >
                <DocumentDuplicateIcon class="inline w-4 mr-2" /> Duplicate
              </button>

              <button
                class="block rounded-md w-full text-left py-1 px-2 hover:cursor-pointer hover:bg-white"
                @click="() => { openDeleteModal(); close(); }"
              >
                <TrashIcon class="inline w-4 mr-2" /> Delete
              </button>
            </div>
          </PopoverPanel>
        </transition>
      </Popover>
    </span>
  </div>

  <div ref="widgetContent" class="widget-content grow h-full w-full z-0" :class="widgetContentClass">

    <div v-if="dataLoading" class="flex justify-center items-center w-full h-full">
      <div class="btn-loader w-10 h-10"></div>
    </div>

    <GraphedValueWidget
      v-if="isGraphWidget"
      :Settings="props.Settings"
      :ReloadData="reloadDataTrigger"
      :RezizeTrigger="resizeTrigger"
      :ExportDataTrigger="exportWidgetDataTrigger"
      @isDataLoading="isDataLoading"
      @dataReloaded="reloadWidgetDataComplete"
      @dataExported="exportDataComplete"
      ref="graphWidget"
    />

    <AccumulatedValueWidget
      v-else-if="props.Settings.Type === 'value'"
      :Settings="props.Settings"
      :ReloadData="reloadDataTrigger"
      @isDataLoading="isDataLoading"
      @dataReloaded="reloadWidgetDataComplete"
      ref="valueWidget"
    />

  </div>


  <!-- Confirm Delete Modal -->
  <GDialog v-model="isDeleteModalOpen" transition="custom-from-bottom-transition">
    <div class="fixed inset-0 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center text-center">
        <div class="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <h3 class="text-lg font-bold leading-6 text-gray-900">Confirm Delete</h3>

          <div class="mt-8">
            <h2 class="font-bold mb-2">Are you sure?</h2>
            <p class="text-sm mb-4">
              This action will remove this data widget from your account.
            </p>

            <div v-if="showDeleteError" class="text-alert text-left text-xs mb-2">
              <ExclamationTriangleIcon class="inline h-6 w-6" />
              Problem removing data widget. Please try again.
            </div>

            <button
              type="button"
              class="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white mr-4 transition hover:bg-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              :disabled="showDeleteLoading"
              @click="confirmDelete"
            >
              <span v-if="showDeleteLoading">One moment <span class="btn-loader"></span></span>
              <span v-else>Confirm</span>
            </button>

            <button
              type="button"
              class="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium transition text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </GDialog>
  <!-- /Confirm Delete Modal -->


  <!-- Info Modal -->
  <GDialog v-model="isInfoModalOpen" transition="custom-from-bottom-transition">
    <div class="fixed inset-0 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center text-center">
        <div class="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <div class="mt-8">
            <h2 class="font-bold mb-2">{{ props.Settings.FullName }}</h2>
            <p class="text-sm mb-4">{{ props.Settings.Comment }}</p>

            <button
              type="button"
              class="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium transition text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              @click="isInfoModalOpen = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </GDialog>
  <!-- /Info Modal -->

</template>

<script setup>

/*** External imports ***/
import { ref, watchEffect, computed } from "vue";
import { useStore } from 'vuex';
import { useRouter } from "vue-router";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { ArrowPathIcon, ArrowsPointingOutIcon, ExclamationTriangleIcon, InformationCircleIcon, EllipsisVerticalIcon } from '@heroicons/vue/20/solid';
import { ArrowUpTrayIcon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { format, addHours } from 'date-fns';
import { convertZonedTimetoUTC } from '@/scripts/dateHelpers';

// ----- Props -----

const props = defineProps({
  WidgetId: String,
  Settings: Object, 
  ResizeTrigger: Boolean,
  WidgetDeletedTrigger: Boolean,
  WidgetDeleteFailedTrigger: String   // Id of the widget whose delete failed, or null
});

// ----- Emits -----

const emit = defineEmits(['deleteWidget', 'editWidget', 'duplicateWidget', 'widgetResizedComplete', 'deleteFailedAcknowledged']);

// ----- Store / Router -----

const store = useStore();
const router = useRouter();
const meter = computed(() => store.getters.meter);
const userName = computed(() => store.state.loggedinEmail.toLowerCase());

// ----- State -----

const widgetContent = ref(null);
const graphWidget = ref(null);
const valueWidget = ref(null);

const dataLoading = ref(true);
const isDeleteModalOpen = ref(false);
const isInfoModalOpen = ref(false);
const showDeleteLoading = ref(false);
const showDeleteError = ref(false);   // FIX: was referenced in template but never declared

const reloadDataTrigger = ref(false);
const exportWidgetDataTrigger = ref(false);
const resizeTrigger = ref(false);

const widgetContentClass = ref('overflow-hidden');

// FIX: plain const — was a ref that never changed
const widgetPath = "/mymeters/managemeter/viewdatawidget";

// ----- Computed -----

// FIX: replaces repeated inline `props.Settings.Type === 'graph'` checks in template
const isGraphWidget = computed(() => props.Settings.Type === 'graph');

const graphOptions = computed(() => graphWidget.value?.chartOptions);

const thisWidget = computed(() => ({
  id: props.WidgetId,
  settings: props.Settings,
  chartData: graphOptions.value
}));

const widgetResized = computed(() => props.ResizeTrigger);
const widgetDeletedTrigger = computed(() => props.WidgetDeletedTrigger);
const widgetDeleteFailedTrigger = computed(() => props.WidgetDeleteFailedTrigger);

// ----- Watchers -----

watchEffect(() => {
  if (widgetResized.value) {
    emit('widgetResizedComplete');
  }

  if (widgetDeletedTrigger.value) {
    isDeleteModalOpen.value = false;
    showDeleteLoading.value = false;
  }

  // Only the widget named by the trigger reacts — otherwise another widget would
  // consume the failure and ack it before this one ever saw it.
  if (widgetDeleteFailedTrigger.value && widgetDeleteFailedTrigger.value === props.WidgetId) {
    showDeleteLoading.value = false;
    showDeleteError.value = true;
    emit('deleteFailedAcknowledged');
  }
});

// ----- Delete -----

function openDeleteModal() {
  showDeleteError.value = false;
  isDeleteModalOpen.value = true;
}

function closeDeleteModal() {
  isDeleteModalOpen.value = false;
}

function confirmDelete() {
  showDeleteLoading.value = true;
  showDeleteError.value = false;
  emit('deleteWidget');
}

// ----- Loading -----

// FIX: was called as isDataLoading(val) in template where val was never defined;
// now passed directly as the event handler so Vue supplies the emitted value
function isDataLoading(val) {
  dataLoading.value = val;
}

// ----- Reload -----

function reloadWidgetData() {
  dataLoading.value = true;
  reloadDataTrigger.value = true;
  widgetContentClass.value = 'overflow-hidden';
}

function reloadWidgetDataComplete() {
  dataLoading.value = false;
  reloadDataTrigger.value = false;
  widgetContentClass.value = 'overflow-none';
}

// ----- Navigation -----

function redirectToWidget() {
  store.dispatch("mutation", ["currentDashboard", thisWidget.value]);
  router.push(widgetPath);
}

// ----- Edit / Duplicate -----

// FIX: removed dead editWidgetTrigger / duplicateWidgetTrigger refs that were set but never read
function editWidget() {
  emit('editWidget', thisWidget.value);
}

function duplicateWidget() {
  emit('duplicateWidget', thisWidget.value);
}

// ----- Time Interval Helpers -----

function getTimeIntervalStart() {
  return props.Settings.TimeInterval
    ? addHours(new Date(), parseInt(props.Settings.TimeInterval)).toISOString()
    : props.Settings.CustomTimeIntervalStart;
}

function getTimeIntervalEnd() {
  return props.Settings.TimeInterval
    ? new Date().toISOString()
    : props.Settings.CustomTimeIntervalEnd;
}

// FIX: verbose manual string concatenation replaced with format() pattern strings
function buildDateTimeParts(date) {
  return {
    day: format(date, "yyyy-MM-dd"),
    time: format(date, "HH:mm")
  };
}

// FIX: returnVal was declared but never used — removed
// FIX: exportWidgetDataTrigger is now set before dispatch so child watchers fire correctly
async function exportData() {
  const thisParams = props.Settings.Parameters.map(p => p.param);

  const startUTC = convertZonedTimetoUTC(getTimeIntervalStart(), meter.value.timeZone.offset);
  const endUTC = convertZonedTimetoUTC(getTimeIntervalEnd(), meter.value.timeZone.offset);

  const { day: startDay, time: startTime } = buildDateTimeParts(startUTC);
  const { day: endDay, time: endTime } = buildDateTimeParts(endUTC);

  exportWidgetDataTrigger.value = true;  // FIX: was never set before dispatch

  await store.dispatch("s3_API", {
    request: "s3_DATA",
    username: userName.value,
    metername: meter.value.name,
    startDay,
    startTime,
    endDay,
    endTime,
    isParseFifteen: true,
    topics: thisParams,
    csv: props.Settings.FullName
  });
}

function exportDataComplete() {
  exportWidgetDataTrigger.value = false;
}

</script>